import React from 'react';
import { motion } from 'framer-motion';
import { mdiBriefcase, mdiFolder } from '@mdi/js';
import Icon from '@mdi/react';
import Header from "../../components/header";

// Make sure your image imports are correct
// Replace these with your actual image paths
import appImage from './../../assets/app.png';
import webImage from './../../assets/web.png';

const Portfolio = () => {
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        when: "beforeChildren"
      }
    }
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  const cardHoverVariants = {
    hover: {
      y: -10,
      boxShadow: "0 15px 30px rgba(0, 0, 0, 0.1)"
    }
  };

  const projects = [
    {
      id: 1,
      title: "Giyani Market App",
      icon: mdiBriefcase,
      description: "The Giyani Market app enhances the shopping experience by providing users with real-time access to local market listings, special offers, and product availability.",
      features: [
        "User-Friendly Interface",
        "Real-Time Updates",
        "Secure Payment Options"
      ],
      image: appImage
    },
    {
      id: 2,
      title: "Roomigle Website",
      icon: mdiFolder,
      description: "Roomigle is an accommodation website that simplifies the booking process, offering users a seamless experience for finding and reserving accommodations.",
      features: [
        "Intuitive Booking System",
        "Responsive Design",
        "User Reviews and Ratings"
      ],
      image: webImage
    }
  ];

  return (
    <motion.section 
      className="portfolio-section"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    ><Header/>
      <div className="portfolio-container">
        <motion.div 
          className="portfolio-header"
          initial={{ y: -30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="portfolio-title">Our Portfolio</h1>
          <p className="portfolio-subtitle">Showcasing Our Best Projects</p>
        </motion.div>

        <motion.div 
          className="portfolio-grid"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {projects.map((project) => (
            <motion.div 
              key={project.id}
              className="portfolio-card"
              variants={itemVariants}
              whileHover={cardHoverVariants.hover}
              transition={{ duration: 0.3, ease: "easeOut" }}
            >
              <div className="card-icon">
                <Icon path={project.icon} size={1.5} color="#5cb6f9" />
              </div>
              <h2 className="card-title">{project.title}</h2>
              <div className="card-image-container">
                <img 
                  src={project.image} 
                  alt={project.title} 
                  className="card-image"
                />
              </div>
              <p className="card-description">{project.description}</p>
              <ul className="card-features">
                {project.features.map((feature, index) => (
                  <li key={index} className="feature-item">
                    <span className="feature-bullet">•</span> {feature}
                  </li>
                ))}
              </ul>
              <motion.button 
                className="card-button"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                View Case Study
              </motion.button>
            </motion.div>
          ))}
        </motion.div>
      </div>

      <style jsx>{`
        .portfolio-section {
          padding: 100px 0;
          background-color: white;
          position: relative;
          overflow: hidden;
        }
        
        .portfolio-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 20px;
        }
        
        .portfolio-header {
          text-align: center;
          margin-bottom: 60px;
        }
        
        .portfolio-title {
          font-size: 3rem;
          font-weight: 700;
          margin-bottom: 15px;
          color: #01224a;
          background: linear-gradient(90deg, #5cb6f9, #01224a);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        
        .portfolio-subtitle {
          font-size: 1.25rem;
          color: #666;
          max-width: 600px;
          margin: 0 auto;
        }
        
        .portfolio-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
          gap: 40px;
          justify-content: center;
        }
        
        .portfolio-card {
          background: white;
          border-radius: 12px;
          padding: 30px;
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
          border: 1px solid rgba(0, 0, 0, 0.05);
          transition: all 0.3s ease;
          display: flex;
          flex-direction: column;
          align-items: center;
          position: relative;
          overflow: hidden;
        }
        
        .portfolio-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: linear-gradient(45deg, transparent, rgba(92, 182, 249, 0.05), transparent);
          transform: translateX(-100%);
          transition: transform 0.6s ease;
        }
        
        .portfolio-card:hover::before {
          transform: translateX(100%);
        }
        
        .card-icon {
          width: 60px;
          height: 60px;
          border-radius: 50%;
          background: rgba(92, 182, 249, 0.1);
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 20px;
        }
        
        .card-title {
          font-size: 1.5rem;
          color: #01224a;
          margin-bottom: 20px;
          text-align: center;
          font-weight: 600;
        }
        
        .card-image-container {
          width: 100%;
          margin-bottom: 20px;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
        }
        
        .card-image {
          width: 100%;
          height: auto;
          display: block;
          transition: transform 0.3s ease;
        }
        
        .portfolio-card:hover .card-image {
          transform: scale(1.02);
        }
        
        .card-description {
          font-size: 1.1rem;
          color: #555;
          text-align: center;
          margin-bottom: 20px;
          line-height: 1.6;
        }
        
        .card-features {
          list-style: none;
          padding: 0;
          margin: 0 0 25px 0;
          width: 100%;
        }
        
        .feature-item {
          font-size: 1rem;
          color: #444;
          margin-bottom: 10px;
          display: flex;
          align-items: flex-start;
          line-height: 1.5;
        }
        
        .feature-bullet {
          color: #5cb6f9;
          margin-right: 8px;
          font-weight: bold;
        }
        
        .card-button {
          background: linear-gradient(135deg, #5cb6f9, #01224a);
          color: white;
          border: none;
          padding: 12px 30px;
          border-radius: 30px;
          font-size: 1rem;
          font-weight: 500;
          cursor: pointer;
          margin-top: auto;
          transition: all 0.3s ease;
          box-shadow: 0 4px 15px rgba(92, 182, 249, 0.3);
        }
        
        .card-button:hover {
          box-shadow: 0 6px 20px rgba(92, 182, 249, 0.4);
        }
        
        @media (max-width: 768px) {
          .portfolio-section {
            padding: 60px 0;
          }
          
          .portfolio-title {
            font-size: 2.2rem;
          }
          
          .portfolio-subtitle {
            font-size: 1.1rem;
          }
          
          .portfolio-grid {
            grid-template-columns: 1fr;
            gap: 30px;
          }
          
          .portfolio-card {
            padding: 25px;
          }
          
          .card-title {
            font-size: 1.3rem;
          }
          
          .card-description {
            font-size: 1rem;
          }
        }
        
        @media (max-width: 480px) {
          .portfolio-title {
            font-size: 1.8rem;
          }
          
          .card-button {
            padding: 10px 25px;
            font-size: 0.9rem;
          }
        }
      `}</style>
    </motion.section>
  );
};

export default Portfolio;